import { useAuth } from "@/lib/store";
import { hasPermission } from "@/lib/permissions";
import { billingAccessCopy } from "@/lib/billingAccessCopy";
import { useEffect, useState } from "react";
import { ArrowRight, Coins, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { setInsufficientCreditsHandler } from "@/lib/apiClient";
import { useLocalization } from "@/lib/localization";

const COPY = {
  en: { badge: "Credits required", title: "A quick top-up will keep you moving", body: "Your remaining credits are not enough for this action. Add a credit pack to continue with smart tools.", safeTitle: "No credits were charged", safeBody: "Your work is safe and remains exactly as it was.", action: "Add credits", close: "Not now" },
  fr: { badge: "Crédits requis", title: "Un rechargement suffit pour continuer", body: "Vos crédits restants ne suffisent pas pour cette action. Ajoutez un pack pour continuer à utiliser les outils intelligents.", safeTitle: "Aucun crédit n’a été débité", safeBody: "Votre travail est sécurisé et reste inchangé.", action: "Ajouter des crédits", close: "Pas maintenant" },
  ar: { badge: "الرصيد مطلوب", title: "إضافة سريعة للرصيد تتيح لك المتابعة", body: "رصيدك المتبقي لا يكفي لهذه العملية. أضف باقة رصيد لمتابعة استخدام الأدوات الذكية.", safeTitle: "لم يتم خصم أي رصيد", safeBody: "عملك آمن وبقي كما هو دون تغيير.", action: "إضافة رصيد", close: "ليس الآن" },
  ha: { badge: "Ana buƙatar kiredit", title: "Ƙarin kiredit kaɗan zai sa ka ci gaba", body: "Kiredit ɗin da ya rage bai isa wannan aikin ba. Ƙara kunshin kiredit don ci gaba da amfani da kayan aikin wayo.", safeTitle: "Ba a cire kiredit ba", safeBody: "Aikinka yana nan lafiya kamar yadda yake.", action: "Ƙara kiredit", close: "Ba yanzu ba" },
  ig: { badge: "Achọrọ kredit", title: "Obere mgbakwunye ga-eme ka ị gaa n’ihu", body: "Kredit fọdụrụ ezughị maka ọrụ a. Tinye ngwugwu kredit ka ị gaa n’ihu na ngwa ndị nwere ọgụgụ isi.", safeTitle: "Ewepụghị kredit ọ bụla", safeBody: "Ọrụ gị dị nchebe ma dịkwa ka ọ dịbu.", action: "Tinye kredit", close: "Ọ bụghị ugbu a" },
  pt: { badge: "Créditos necessários", title: "Um reforço rápido permite continuar", body: "Os créditos restantes não chegam para esta ação. Adicione um pacote para continuar a usar as ferramentas inteligentes.", safeTitle: "Nenhum crédito foi cobrado", safeBody: "O seu trabalho está seguro e permanece inalterado.", action: "Adicionar créditos", close: "Agora não" },
  sw: { badge: "Salio linahitajika", title: "Ongeza salio kidogo ili uendelee", body: "Salio lililobaki halitoshi kwa hatua hii. Ongeza kifurushi ili kuendelea kutumia zana mahiri.", safeTitle: "Hakuna salio lililokatwa", safeBody: "Kazi yako iko salama na haijabadilishwa.", action: "Ongeza salio", close: "Sio sasa" },
  yo: { badge: "A nílò kírẹ́díìtì", title: "Ìfikún díẹ̀ yóò jẹ́ kí o tẹ̀síwájú", body: "Kírẹ́díìtì tó kù kò tó fún iṣẹ́ yìí. Fi àpò kírẹ́díìtì kún un láti tẹ̀síwájú pẹ̀lú irinṣẹ́ ọlọ́gbọ́n.", safeTitle: "A kò yọ kírẹ́díìtì kankan", safeBody: "Iṣẹ́ rẹ wà láìléwu, kò sì yí padà.", action: "Fi kírẹ́díìtì kún", close: "Kì í ṣe báyìí" },
  zu: { badge: "Kudingeka amakhredithi", title: "Ukwengeza kancane kuzokugcina uqhubeka", body: "Amakhredithi asele awanele kulesi senzo. Engeza iphakethe ukuze uqhubeke namathuluzi ahlakaniphile.", safeTitle: "Akukho khredithi ekhokhisiwe", safeBody: "Umsebenzi wakho uphephile futhi awushintshanga.", action: "Engeza amakhredithi", close: "Hhayi manje" },
};

export function InsufficientCreditsDialog() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const canManageBilling = hasPermission(session, "billing");
  const { language } = useLocalization();
  const copy = COPY[language] || COPY.en;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInsufficientCreditsHandler(() => {
      toast.dismiss();
      setOpen(true);
      window.setTimeout(() => toast.dismiss(), 0);
    });
    return () => setInsufficientCreditsHandler(null);
  }, []);

  const openCredits = () => {
    setOpen(false);
    if (canManageBilling) navigate("/credits");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[450px] overflow-hidden border-0 bg-card p-0 shadow-[0_32px_100px_-34px_rgba(6,78,59,0.72)] sm:rounded-[30px]">
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-950 via-primary to-emerald-700 px-7 pb-8 pt-7 text-white">
          <div className="absolute -right-14 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-emerald-300/10" />
          <div className="relative flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-[20px] border border-white/20 bg-white/15 shadow-lg">
              <Coins className="h-6 w-6" />
            </div>
            <div className="mt-5 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
              {copy.badge}
            </div>
            <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight tracking-tight text-white">
              {copy.title}
            </DialogTitle>
            <DialogDescription className="mt-3 max-w-sm text-sm leading-6 text-white/80">
              {canManageBilling ? copy.body : billingAccessCopy[language].message}
            </DialogDescription>
          </div>
        </div>

        <div className="space-y-5 px-7 pb-7 pt-6">
          <div className="flex items-start gap-4 rounded-2xl border border-primary/15 bg-primary/5 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-foreground">{copy.safeTitle}</div>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">{copy.safeBody}</p>
            </div>
          </div>

          {canManageBilling ? (
            <Button className="h-12 w-full rounded-xl text-sm font-semibold" onClick={openCredits}>
              {copy.action}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : null}
          <button
            className="w-full text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setOpen(false)}
            type="button"
          >
            {copy.close}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
